import React, { useState, useEffect } from 'react';
import { getTodaysOBDs } from '../utils/syntheticDataReader';
import {
  Container,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { getOBDDetails } from '../utils/syntheticDataReader';

const ProductivityTracking = () => {
  const [obdTasks, setObdTasks] = useState([]);
  const [productivityData, setProductivityData] = useState([]);
  const [totalObdsToday, setTotalObdsToday] = useState(0);

  useEffect(() => {
    loadProductivityData();
    loadTodaysTotals();
  }, []);

  const loadTodaysTotals = async () => {
    try {
      const todaysOBDs = await getTodaysOBDs();
      setTotalObdsToday(todaysOBDs.length);
    } catch (error) {
      console.error('Error loading total OBDs:', error);
    }
  };

  const loadProductivityData = async () => {
    console.log('=== LOADING PRODUCTIVITY DATA FROM SIGN-ON ===');
    
    // Get all completion data across all dates
    const completionDataByDate = JSON.parse(localStorage.getItem('obdSignOnData') || '{}');
    const allCompletedObds = [];
    
    // Collect completed OBDs from all dates
    Object.entries(completionDataByDate).forEach(([date, obds]) => {
      const completedObds = obds.filter(obd => obd.endTime && obd.startTime && obd.associateName);
      allCompletedObds.push(...completedObds);
    });
    
    console.log('Loading productivity for', allCompletedObds.length, 'completed OBDs across all dates');

    if (allCompletedObds.length === 0) {
      setObdTasks([]);
      setProductivityData([]);
      return;
    }

    const productivityByAssociate = {};

    for (const obd of allCompletedObds) {
      console.log('Processing OBD:', obd.obdNumber, 'for', obd.associateName);
      
      // Use the totalLines and totalUnits from the OBD if available (from STO data)
      // Otherwise try to fetch from synthetic data
      let totalLines = obd.totalLines;
      let totalUnits = obd.totalUnits;
      
      if (!totalLines || !totalUnits) {
        const details = await getOBDDetails(obd.obdNumber);
        if (details) {
          totalLines = details.totalLines;
          totalUnits = details.totalUnits;
          console.log('Loaded from synthetic data:', totalLines, 'lines,', totalUnits, 'units');
        } else {
          console.log('No details found for OBD:', obd.obdNumber);
          totalLines = 0;
          totalUnits = 0;
        }
      }

      const durationMs = new Date(obd.endTime) - new Date(obd.startTime);
      const durationHours = Math.max(0, durationMs / (1000 * 60 * 60));

      if (!productivityByAssociate[obd.associateName]) {
        productivityByAssociate[obd.associateName] = {
          name: obd.associateName,
          totalLines: 0,
          totalUnits: 0,
          totalHours: 0,
          obdCount: 0
        };
      }

      const assocData = productivityByAssociate[obd.associateName];
      assocData.totalLines += totalLines;
      assocData.totalUnits += totalUnits;
      assocData.totalHours += durationHours;
      assocData.obdCount++;
    }

    console.log('Productivity by associate:', productivityByAssociate);

    const productivity = Object.values(productivityByAssociate).map(assoc => ({
      ...assoc,
      unitsPerHour: assoc.totalHours > 0 ? (assoc.totalUnits / assoc.totalHours).toFixed(1) : 0,
      linesPerHour: assoc.totalHours > 0 ? (assoc.totalLines / assoc.totalHours).toFixed(1) : 0,
      avgPickTime: assoc.totalLines > 0 ? ((assoc.totalHours * 60) / assoc.totalLines).toFixed(1) : 0
    }));

    setProductivityData(productivity);
    setObdTasks(allCompletedObds);
  };

  const hourlyData = productivityData.map(emp => ({
    name: emp.name,
    unitsPerHour: parseFloat(emp.unitsPerHour),
    linesPerHour: parseFloat(emp.linesPerHour)
  }));

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Productivity Tracking
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Monitor picker performance and efficiency metrics
          </Typography>
        </Grid>

        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Total Associates
              </Typography>
              <Typography variant="h3" sx={{ color: '#1976d2' }}>
                {productivityData.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                OBDs: Completed / Total
              </Typography>
              <Typography variant="h3" sx={{ color: '#1976d2' }}>
                {obdTasks.length} / {totalObdsToday}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {totalObdsToday - obdTasks.length} Remaining
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Avg Units/Hour
              </Typography>
              <Typography variant="h3" sx={{ color: '#1976d2' }}>
                {productivityData.length > 0
                  ? (productivityData.reduce((sum, emp) => sum + parseFloat(emp.unitsPerHour), 0) / productivityData.length).toFixed(1)
                  : '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Avg Lines/Hour
              </Typography>
              <Typography variant="h3" sx={{ color: '#1976d2' }}>
                {productivityData.length > 0
                  ? (productivityData.reduce((sum, emp) => sum + parseFloat(emp.linesPerHour), 0) / productivityData.length).toFixed(1)
                  : '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Units Per Hour Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Units Per Hour by Associate
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="unitsPerHour" fill="#1976d2" name="Units/Hour" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Lines Per Hour Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Lines Per Hour by Associate
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="linesPerHour" fill="#26a69a" name="Lines/Hour" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Detailed Productivity Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Detailed Productivity Metrics
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Associate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>OBDs Completed</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Lines</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Units</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Hours</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Units/Hour</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Lines/Hour</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Pick Time (min/line)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productivityData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="textSecondary">
                          No productivity data available. Complete some OBD tasks to see metrics.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    productivityData
                      .sort((a, b) => parseFloat(b.unitsPerHour) - parseFloat(a.unitsPerHour))
                      .map((emp) => (
                        <TableRow key={emp.name}>
                          <TableCell sx={{ fontWeight: 500 }}>{emp.name}</TableCell>
                          <TableCell align="right">{emp.obdCount}</TableCell>
                          <TableCell align="right">{emp.totalLines}</TableCell>
                          <TableCell align="right">{emp.totalUnits}</TableCell>
                          <TableCell align="right">{emp.totalHours.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            {emp.unitsPerHour}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#26a69a' }}>
                            {emp.linesPerHour}
                          </TableCell>
                          <TableCell align="right">{emp.avgPickTime}</TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProductivityTracking;